/** Laravel API base — no trailing slash. */
const PRODUCTION_API = "https://api.simcollect.com";
const LOCAL_API = "http://127.0.0.1:8000";

function isBrowserLocalHost() {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
}

function isLocalApiUrl(url) {
  return !url || /^(https?:\/\/)?(localhost|127\.0\.0\.1)(:\d+)?\/?$/i.test(url);
}

function resolveApiBaseUrl() {
  const envUrl = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "");

  if (typeof window !== "undefined") {
    if (isBrowserLocalHost()) {
      return isLocalApiUrl(envUrl) ? envUrl || LOCAL_API : envUrl;
    }
    // Live/staging hosts must never call 127.0.0.1, even if the build env was wrong.
    return isLocalApiUrl(envUrl) ? PRODUCTION_API : envUrl || PRODUCTION_API;
  }

  return envUrl || (process.env.NODE_ENV === "development" ? LOCAL_API : PRODUCTION_API);
}

export const API_BASE_URL = resolveApiBaseUrl();

/** When true, login uses local mock OTP instead of the API. */
export const USE_MOCK_AUTH = process.env.REACT_APP_USE_MOCK_AUTH === "true";

export const OPS_AUTH = {
  /** Step 1 — request OTP (+ operator profile in response) */
  request: "/api/ops/auth/request",
  verify: "/api/ops/auth/verify",
  resend: "/api/ops/auth/resend-otp",
  logout: "/api/ops/logout",
  profile: "/api/ops/profile",
  live: "/api/ops/live",
  permissions: "/api/ops/permissions",
  allRoles: "/api/ops/all_roles",
  createRole: "/api/ops/create_roles",
  updateRole: "/api/ops/update_roles",
  deleteRole: "/api/ops/delete_roles",
  register: "/api/ops/auth/register",
  teamMembers: "/api/ops/all/team_member",
  updateTeamMember: "/api/ops/update/team_member",
  statusTeamMember: "/api/ops/status/team_member",
  deleteTeamMember: "/api/ops/destroy/team_member",
  restoreTeamMember: "/api/ops/restore/team_member",
  institutions: "/api/ops/institutions",
  institutionsSummary: "/api/ops/institutions/summary",
  customers: "/api/ops/customers",
  customersSummary: "/api/ops/customers/summary",
  agents: "/api/ops/agents",
  cases: "/api/ops/cases",
  casesSummary: "/api/ops/cases/summary",
  bulkAssign: "/api/ops/cases/bulk-assign",
  bulkAssignPreview: "/api/ops/cases/bulk-assign/preview",
  escalations: "/api/ops/escalations",
  dashboardOverview: "/api/ops/dashboard/overview",
  followUps: "/api/ops/follow-ups",
  dashboardReport: "/api/ops/dashboard/report",
  payments: "/api/ops/payments",
  paymentsSummary: "/api/ops/payments/summary",
  paymentGateway: "/api/ops/payment-gateway",
  paymentGatewayToken: "/api/ops/payment-gateway/token",
  preApprovals: "/api/ops/pre-approvals",
  preApprovalHistory: "/api/ops/pre-approvals/history",
  paymentPlans: "/api/ops/payment-plans",
  callLogs: "/api/ops/call-logs",
  callLogsSummary: "/api/ops/call-logs/summary",
  activityLogs: "/api/ops/activity-logs",
  activityLogsFilters: "/api/ops/activity-logs/filters",
  activityLogsSummary: "/api/ops/activity-logs/summary",
  activityLogsSla: "/api/ops/activity-logs/sla",
  reports: "/api/ops/reports",
};

export function opsReportPath(slug) {
  return `/api/ops/reports/${encodeURIComponent(slug)}`;
}

export function institutionCustomersPath(slug) {
  return `/api/ops/institutions/${encodeURIComponent(slug)}/customers`;
}

export function institutionCustomersSummaryPath(slug) {
  return `/api/ops/institutions/${encodeURIComponent(slug)}/customers/summary`;
}

export function institutionPortfolioGroupsPath(slug) {
  return `/api/ops/institutions/${encodeURIComponent(slug)}/portfolio-groups`;
}

export function institutionPortfolioGroupPath(slug, groupSlug) {
  return `/api/ops/institutions/${encodeURIComponent(slug)}/portfolio-groups/${encodeURIComponent(groupSlug)}`;
}

export function institutionGroupCasesPath(slug, groupSlug) {
  return `${institutionPortfolioGroupPath(slug, groupSlug)}/cases`;
}

export function institutionGroupUploadsPath(slug, groupSlug) {
  return `${institutionPortfolioGroupPath(slug, groupSlug)}/file-uploads`;
}

export function institutionFileRecordsPath(slug, groupSlug, uploadId) {
  return `${institutionGroupUploadsPath(slug, groupSlug)}/${encodeURIComponent(uploadId)}/records`;
}

export function caseAssignPath(caseId) {
  return `/api/ops/cases/${encodeURIComponent(caseId)}/assign`;
}

export function casePath(caseId) {
  return `/api/ops/cases/${encodeURIComponent(caseId)}`;
}

export function caseSlaPath(caseId) {
  return `/api/ops/cases/${encodeURIComponent(caseId)}/sla`;
}

export function caseCallLogsPath(caseId) {
  return `/api/ops/cases/${encodeURIComponent(caseId)}/call-logs`;
}

export function casePaymentInitiatePath(caseId) {
  return `/api/ops/cases/${encodeURIComponent(caseId)}/payments/initiate`;
}

export function paymentUpdatePath(paymentId) {
  return `/api/ops/payments/${encodeURIComponent(paymentId)}`;
}

export function paymentStatusPath(paymentId) {
  return `/api/ops/payments/${encodeURIComponent(paymentId)}/status`;
}

export function casePaymentsPath(caseId) {
  return `/api/ops/cases/${encodeURIComponent(caseId)}/payments`;
}

export function casePreApprovalPath(caseId) {
  return `/api/ops/cases/${encodeURIComponent(caseId)}/pre-approvals`;
}

export function casePaymentPlanPath(caseId) {
  return `/api/ops/cases/${encodeURIComponent(caseId)}/payment-plans`;
}

export function preApprovalStatusPath(referenceId) {
  return `/api/ops/pre-approvals/${encodeURIComponent(referenceId)}/status`;
}

export function preApprovalUpdatePath(referenceId) {
  return `/api/ops/pre-approvals/${encodeURIComponent(referenceId)}`;
}

export function paymentPlanUpdatePath(planId) {
  return `/api/ops/payment-plans/${encodeURIComponent(planId)}`;
}

export function caseEscalatePath(caseId) {
  return `/api/ops/cases/${encodeURIComponent(caseId)}/escalate`;
}
